import {
	getFont,
	getTextWidth,
} from '@dealstack/frontend/app/pages/Valuations/shared/textMeasurement.utils';

export const ICON_RIGHT_MARGIN = 4;
export const REMOVE_ICON_LEFT_MARGIN = 4;
export const LEFT_RIGHT_PADDING = 8;
export const BORDER = 1;

export const GAP = 4;
export const ICON_SIZE = 14;
export const CLOSE_ICON_SIZE = 11;
export const BADGE_HEIGHT = 20;

export const measureBadgeWidth = ({
	ref,
	label,
	hasRemove,
	hasIcon,
}: {
	ref: React.RefObject<HTMLDivElement>;
	label: string;
	hasRemove: boolean;
	hasIcon: boolean;
}) => {
	if (!ref.current) {
		return 0;
	}

	const font = getFont(ref.current);
	// using Math.ceil to avoid sub-pixel issues
	const textWidth = Math.ceil(getTextWidth(label, font));

	const badgeWidth =
		BORDER +
		LEFT_RIGHT_PADDING +
		(hasIcon ? ICON_SIZE + ICON_RIGHT_MARGIN : 0) +
		textWidth +
		(hasRemove ? CLOSE_ICON_SIZE + REMOVE_ICON_LEFT_MARGIN : 0) +
		LEFT_RIGHT_PADDING +
		BORDER;
	return badgeWidth;
};

export const getPxOrPercent = (cssValue: string): { type: 'px' | 'percent'; value: number } => {
	const numericValue = cssValue.replace(/[^\d.]/g, '');
	const unit = cssValue.replace(numericValue, '');

	if (unit === 'px') {
		return { type: 'px', value: Number(numericValue) };
	} else if (unit === '%') {
		return { type: 'percent', value: Number(numericValue) };
	}

	return { type: 'px', value: 0 };
};

export const mutateValueAndFraction = ({
	length,
	fraction,
	cssValue,
}: {
	length: number;
	fraction: number;
	cssValue: string;
}): { length: number; fraction: number } => {
	const { type, value } = getPxOrPercent(cssValue);

	if (type === 'px' && length === 0) {
		const newLength = value;
		// If we have a percentage waiting, calculate it now
		if (fraction && fraction > 0) {
			return { length: fraction * newLength, fraction: 0 };
		}
		return { length: newLength, fraction: 1 };
	} else if (type === 'percent' && length === 0) {
		return { length: 0, fraction: fraction * (value / 100) };
	}

	return { length, fraction };
};

export const estimateElementMaxAvailableSpace = (
	element: HTMLElement
): { maxWidth: number; maxHeight: number } => {
	let currentElement: HTMLElement | null = element;
	let maxWidth = 0;
	let maxHeight = 0;
	let widthFraction = 1;
	let heightFraction = 1;

	// Traverse up the tree once, collecting all constraints
	while (currentElement && (maxWidth === 0 || maxHeight === 0)) {
		const computedStyle = getComputedStyle(currentElement);

		// Process max-width
		const maxWidthResult = mutateValueAndFraction({
			length: maxWidth,
			fraction: widthFraction,
			cssValue:
				computedStyle.maxWidth === 'none' ? computedStyle.width : computedStyle.maxWidth,
		});
		maxWidth = maxWidthResult.length;
		widthFraction = maxWidthResult.fraction;

		// Process max-height
		const maxHeightResult = mutateValueAndFraction({
			length: maxHeight,
			fraction: heightFraction,
			cssValue:
				computedStyle.maxHeight === 'none' ? computedStyle.height : computedStyle.maxHeight,
		});
		maxHeight = maxHeightResult.length;
		heightFraction = maxHeightResult.fraction;

		currentElement = currentElement.parentElement;
	}

	// Only calculate from viewport if we still have percentages and no pixel values
	if (widthFraction > 0 && maxWidth === 0) {
		maxWidth = widthFraction * window.innerWidth;
	}

	if (heightFraction > 0 && maxHeight === 0) {
		maxHeight = heightFraction * window.innerHeight;
	}

	return { maxWidth, maxHeight };
};

export const maxBadgeCountInContainer = (
	badgeWidths: number[],
	containerHeight: number,
	containerWidth: number
): number => {
	if (containerHeight < BADGE_HEIGHT) {
		// Not enough vertical space to fit a badge
		return 0;
	}
	let currX = -GAP; // ignore the "first gap" that is not present
	let currY = BADGE_HEIGHT;

	for (let i = 0; i < badgeWidths.length; i++) {
		if (badgeWidths[i] > containerWidth) {
			return i;
		}

		currX += GAP + badgeWidths[i];

		if (currX > containerWidth) {
			currX = badgeWidths[i];
			currY += GAP + BADGE_HEIGHT;
			if (currY > containerHeight) {
				// Not enough vertical space to fit one more row
				return i;
			}
		}
	}

	return badgeWidths.length;
};
